import type { MessageKey } from './en';
import type { TranslateVars } from './types';

/**
 * The API answers in English: its messages are also read by API clients and end
 * up in logs, so they are part of the contract and are not localised there.
 * This module maps them onto dictionary keys for display.
 *
 * `tests/i18n.test.ts` feeds invalid input through every shared schema and
 * asserts that each message it produces is covered here — which is what keeps
 * this table honest as the schemas change.
 */
export type Translate = (key: MessageKey, vars?: TranslateVars) => string;

/** Messages that arrive verbatim. */
const EXACT: Record<string, MessageKey> = {
  'Authentication required': 'server.authRequired',
  'Your session has expired. Please sign in again.': 'error.unauthenticated',
  'Cross-site requests are not allowed': 'server.crossSite',
  'You do not have permission to perform this action': 'error.forbidden',
  'Request validation failed': 'server.validationFailed',
  'Invalid request body': 'server.invalidRequest',
  'Invalid query parameters': 'server.invalidRequest',
  'Invalid path parameters': 'server.invalidRequest',
  'Nothing to update': 'server.nothingToUpdate',
  // Zod's own defaults, for a missing key and a malformed identifier.
  Required: 'server.requiredPlain',
  'Invalid uuid': 'server.invalidId',
  'Select at least one test case': 'server.selectAtLeastOneCase',
  'Select at least one item': 'server.selectAtLeastOneItem',
  'Code must be uppercase letters and digits, starting with a letter': 'server.codeFormat',
  'Login may contain letters, digits, dot, underscore and hyphen': 'server.loginFormat',
  'Parent folder does not exist': 'server.parentFolderMissing',
  'Folder does not exist in this project': 'server.folderNotInProject',
  'Environment does not exist in this project': 'server.environmentNotInProject',
  'An environment with this name already exists in this project': 'server.environmentNameTaken',
  'Layer does not exist in this project': 'server.layerNotInProject',
  'Module does not exist in this project': 'server.moduleNotInProject',
  'A layer with this name already exists in this project': 'server.layerNameTaken',
  'A module with this name already exists in this project': 'server.moduleNameTaken',
  'Maximum nesting depth reached': 'server.maxDepthReached',
  'A folder with this name already exists here': 'server.folderNameTaken',
  'A folder cannot be moved inside itself': 'server.folderSelfMove',
  'Checklist does not exist in this project': 'server.checklistNotInProject',
  'Add at least one checklist item first': 'server.checklistNeedsItems',
  'This checklist has no items to run': 'server.checklistEmpty',
  'There is nothing to re-run: the checklist has no items': 'server.nothingToRerun',
  'Assignee does not exist': 'server.assigneeMissing',
  'This login is already taken': 'server.loginTaken',
  'This code is already taken': 'server.codeTaken',
  'This run is already completed. Reopen it or start a new run to record results.':
    'server.runAlreadyCompletedExecute',
  'This run is already completed': 'server.runAlreadyCompleted',
  'This run is already active': 'server.runAlreadyActive',
  'A completed test run cannot be deleted — it is part of the history':
    'server.runCompletedNoDelete',
  'The last active admin cannot be demoted or deactivated': 'server.lastAdminDemote',
  'The last active admin cannot be deleted': 'server.lastAdminDelete',
  'You cannot deactivate your own account': 'server.selfDeactivate',
  'You cannot delete your own account': 'server.selfDelete',
  'A record with these values already exists': 'server.duplicateRecord',
  'Referenced record does not exist or is still in use': 'server.referencedRecord',
  'Value violates a database constraint': 'server.constraintViolation',
  'Something went wrong. Please try again.': 'error.internal',
  'Too many requests. Please try again later.': 'error.rateLimited',
  'Cannot reach the server. Check your connection and try again.': 'error.network',
  'Unexpected server response. Please try again.': 'error.unexpectedResponse',
};

/** The field names that appear inside "<Field> is required". */
const FIELD_KEYS: Record<string, MessageKey> = {
  Login: 'field.login',
  Name: 'field.name',
  Title: 'field.title',
  Code: 'field.code',
  Action: 'field.action',
  Value: 'field.value',
};

/** The entities that appear inside "<Entity> not found". */
const ENTITY_KEYS: Record<string, MessageKey> = {
  Project: 'entity.notFound.project',
  Folder: 'entity.notFound.folder',
  Environment: 'entity.notFound.environment',
  'Test case': 'entity.notFound.testCase',
  Checklist: 'entity.notFound.checklist',
  'Test run': 'entity.notFound.testRun',
  'Run item': 'entity.notFound.runItem',
  User: 'entity.notFound.user',
  Endpoint: 'entity.notFound.endpoint',
  Resource: 'entity.notFound.resource',
};

interface Pattern {
  re: RegExp;
  translate: (match: RegExpMatchArray, t: Translate) => string | null;
}

const PATTERNS: Pattern[] = [
  {
    re: /^(.+) is required$/,
    translate: (match, t) => {
      const fieldKey = FIELD_KEYS[match[1] ?? ''];
      return fieldKey ? t('server.required', { field: t(fieldKey) }) : null;
    },
  },
  {
    re: /^(?:.+ )?must be at most (\d+) characters$/,
    translate: (match, t) => t('server.maxLength', { max: Number(match[1]) }),
  },
  {
    re: /^(?:.+ )?must be at least (\d+) characters$/,
    translate: (match, t) => t('server.minLength', { min: Number(match[1]) }),
  },
  {
    re: /^Folders can be nested up to (\d+) levels deep$/,
    translate: (match, t) => t('server.folderDepth', { max: Number(match[1]) }),
  },
  {
    re: /^Project code (\S+) is already used by "(.+)"$/,
    translate: (match, t) =>
      t('server.projectCodeTaken', { code: match[1] ?? '', name: match[2] ?? '' }),
  },
  {
    re: /^Only the author or a QA Lead can .+$/,
    translate: (_match, t) => t('server.onlyAuthorOrLead'),
  },
  {
    re: /^Your role \(.+\) is not allowed to .+$/,
    translate: (_match, t) => t('error.forbidden'),
  },
  {
    re: /^(.+) not found$/,
    translate: (match, t) => {
      const entityKey = ENTITY_KEYS[match[1] ?? ''];
      return entityKey ? t(entityKey) : null;
    },
  },
];

/** Returns the localised text, or `null` when the message is not recognised. */
export const translateServerMessage = (message: string, t: Translate): string | null => {
  const exact = EXACT[message];
  if (exact) return t(exact);

  for (const pattern of PATTERNS) {
    const match = pattern.re.exec(message);
    if (match) {
      const translated = pattern.translate(match, t);
      if (translated) return translated;
    }
  }

  return null;
};
